import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { ethers } from "ethers";
import { orderProcessorAbi } from "@/abi";
import { useSmartWallet } from "./SmartWallet";
import { useDispatch } from "react-redux";
import {
  fetchOrdersThunk,
  resetAssignedOrderThunk,
  setAssignedOrderThunk,
} from "@/state/orders/thunk";
import notification from "../assets/sound/notification.wav";

interface IOrdersProcessorContext {
  autoOpen: boolean;
  setAutoOpen: (value: boolean) => void;
  refreshOrders: () => void;
  resetAssignOrder: () => void;
}

const initialOrdersProcessorContext: IOrdersProcessorContext = {
  autoOpen: false,
  setAutoOpen: () => undefined,
  refreshOrders: async () => undefined,
  resetAssignOrder: () => {},
};

const ethersProvider = new ethers.providers.JsonRpcProvider(
  import.meta.env.VITE_RPC_URL,
);

const contractInstance = new ethers.Contract(
  import.meta.env.VITE_ORDER_PROCESSOR,
  orderProcessorAbi,
  ethersProvider,
);

const OrderProcessorContext = createContext(initialOrdersProcessorContext);

export const OrderProcessorContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dispatch = useDispatch();
  const { smartWalletAddress } = useSmartWallet();
  const [acceptedOrderAddress, setAcceptedOrderAddress] = useState<
    string | null
  >(null);
  const [acceptedOrderId, setAcceptedOrderId] = useState<number | null>(null);
  const [autoOpen, setAutoOpen] = useState(false);

  const fetchOrders = () => {
    if (smartWalletAddress) {
      dispatch(fetchOrdersThunk({ address: smartWalletAddress }));
    }
  };

  const resetAssignOrder = () => {
    dispatch(resetAssignedOrderThunk());
    setAutoOpen(false);
  };

  useEffect(() => {
    if (smartWalletAddress) fetchOrders();
  }, [smartWalletAddress]);

  useEffect(() => {
    if (smartWalletAddress && contractInstance) {
      const newOrderFilter = contractInstance.filters.MerchantAssignedNewOrder(
        null,
        smartWalletAddress,
        null,
      );
      const reAssignedOrderFilter =
        contractInstance.filters.MerchantReAssignedNewOrder(
          null,
          smartWalletAddress,
        );

      // Subscribing to MerchantAssignedNewOrder event
      contractInstance.on(newOrderFilter, async (orderId, merchant, order) => {
        console.log("MerchantAssignedNewOrder Event:", {
          orderId,
          merchant,
          order,
        });
        new Audio(notification).play();
        dispatch(setAssignedOrderThunk(order));
        setAutoOpen(true);
        fetchOrders();
      });

      // Subscribing to MerchantReAssignedNewOrder event
      contractInstance.on(
        reAssignedOrderFilter,
        async (orderId, merchant, order) => {
          console.log("MerchantReAssignedNewOrder Event:", {
            orderId,
            merchant,
            order,
          });
          new Audio(notification).play();
          dispatch(setAssignedOrderThunk(order));
          setAutoOpen(true);
          fetchOrders();
        },
      );

      // Subscribing to OrderAccepted event
      const orderAcceptedFilter = contractInstance.filters.OrderAccepted(
        null,
        smartWalletAddress,
      );
      contractInstance.on(
        orderAcceptedFilter,
        async (orderId, merchant, _pubKey, order) => {
          console.log("OrderAccepted Event:", {
            orderId,
            merchant,
            _pubKey,
            order,
          });
          dispatch(setAssignedOrderThunk(order));
          setAcceptedOrderId(order.id);
          setAcceptedOrderAddress(order?.user);
          fetchOrders();
        },
      );

      const buyOrderPaidFilter = contractInstance.filters.BuyOrderPaid(
        null,
        acceptedOrderAddress,
        null,
      );
      const sellOrderUpiSetFilter = contractInstance.filters.SellOrderUpiSet(
        null,
        acceptedOrderAddress,
        null,
      );

      // Subscribing to BuyOrderPaid event
      contractInstance.on(buyOrderPaidFilter, async (...args) => {
        console.log("BuyOrderPaid Event: ", args);
        const order = args[2];
        if (order[6] === smartWalletAddress) {
          console.log("BuyOrderPaid Event:", order);
          dispatch(setAssignedOrderThunk(order));
          fetchOrders();
        }
      });

      // Subscribing to SellOrderUpiSet event
      contractInstance.on(sellOrderUpiSetFilter, async (...args) => {
        console.log("SellOrderUpiSet Event: ", args);
        const order = args[2];
        if (order[6] === smartWalletAddress) {
          console.log("SellOrderUpiSet Event:", order);
          dispatch(setAssignedOrderThunk(order));
          fetchOrders();
        }
      });

      const cancelledOrder = contractInstance.filters.CancelledOrders(
        acceptedOrderId,
        null,
      );

      contractInstance.on(cancelledOrder, async (...args) => {
        console.log("CancelledOrders Event: ", args);
        dispatch(setAssignedOrderThunk(args[1]));
        fetchOrders();
      });
    }

    return () => {
      contractInstance.removeAllListeners();
    };
  }, [smartWalletAddress, contractInstance, acceptedOrderId]);

  return (
    <OrderProcessorContext.Provider
      value={{
        autoOpen,
        setAutoOpen: (value: boolean) => setAutoOpen(value),
        refreshOrders: fetchOrders,
        resetAssignOrder,
      }}>
      {children}
    </OrderProcessorContext.Provider>
  );
};

export const useOrders = () => useContext(OrderProcessorContext);
